import { Store } from 'react-notifications-component';

// Track recently shown notifications to prevent duplicates
const recentNotifications = new Map();
const NOTIFICATION_DEDUP_WINDOW = 60000; // 60 seconds - don't show same notification within this window

export const defaultTransaction = {
  duration: 0, // Transition duration in ms.
  timingFunction: 'linear', // CSS timing function for the transition.
  delay: 0, //Delay of the transition in ms.
};

export const defaultDismiss = {
  duration: 2000, // Time in milliseconds after notification gets dismissed (5 seconds).
  onScreen: false, // Show time left directly on the notification.
  showIcon: true, // Show or hide the close icon.
  click: true, // Enable dismissal by click.
  touch: true, // Enable dismiss by touch move.
  pauseOnHover: true, // Hovering over notification will pause the dismiss timer.
};

// https://github.com/teodosii/react-notifications-component
const defaultNotification = {
  title: '', // Title of the notification. Option is ignored if content is set, otherwise it is required.
  message: 'Unknown message', // Message of the notification. Option is ignored if content is set, otherwise it is required.
  type: 'default', // "success" || "danger" || "info" || "default" || "warning"
  insert: 'top', // Specify where to append notification into the container - top or bottom.
  container: 'top-center', // Container in which the notification will be displayed.
  slidingEnter: defaultTransaction, // Transition to be used when sliding to show a notification.
  slidingExit: defaultTransaction, // Transition to be used when sliding to remove a notification.
  touchRevert: defaultTransaction, // Transition to be used when sliding back after an incomplete swipe.
  touchSlidingExit: defaultTransaction, // Transition to be used when sliding on swipe.
  // Note: dismiss is NOT included here - it's always set explicitly in addNotification
};

export const addNotification = (props) => {
  if (!Store || typeof Store.addNotification !== 'function') {
    if (process.env.NODE_ENV === 'development') {
      console.log('Notification store is unavailable, skipping toast notification.');
    }
    return;
  }

  // Create a unique key for this notification based on title and message
  const notificationKey = `${props.title || ''}_${props.message || ''}`;
  const now = Date.now();

  // Check if we've shown this notification recently.
  // Do not suppress danger notifications; users must always see errors.
  // bypassDedup: set true for toasts that should repeat (e.g. same API message on resend).
  const shouldDeduplicate = props.type !== 'danger' && !props.bypassDedup;
  const lastShown = recentNotifications.get(notificationKey);
  if (shouldDeduplicate && lastShown && now - lastShown < NOTIFICATION_DEDUP_WINDOW) {
    // Skip duplicate notification
    if (process.env.NODE_ENV === 'development') {
      console.log('Skipping duplicate notification:', notificationKey);
    }
    return;
  }

  // Record that we're showing this notification
  recentNotifications.set(notificationKey, now);

  // Clean up old entries (older than the dedup window)
  for (const [key, timestamp] of recentNotifications.entries()) {
    if (now - timestamp > NOTIFICATION_DEDUP_WINDOW) {
      recentNotifications.delete(key);
    }
  }

  // Extract dismiss and app-only flags from props before passing to react-notifications-component
  const { dismiss: dismissConfig, bypassDedup: _bypassDedup, ...restProps } = props;

  // Determine the duration - use provided duration if valid (> 0), otherwise use default
  const duration = dismissConfig?.duration > 0 ? dismissConfig.duration : defaultDismiss.duration;

  // Build the final dismiss configuration - ensure all required properties are set
  // Create a new object to avoid reference issues
  const finalDismiss = {
    duration: duration, // Always set duration - this is critical for auto-dismiss
    onScreen:
      dismissConfig?.onScreen !== undefined ? dismissConfig.onScreen : defaultDismiss.onScreen,
    showIcon:
      dismissConfig?.showIcon !== undefined ? dismissConfig.showIcon : defaultDismiss.showIcon,
    click: dismissConfig?.click !== undefined ? dismissConfig.click : defaultDismiss.click,
    touch: dismissConfig?.touch !== undefined ? dismissConfig.touch : defaultDismiss.touch,
    pauseOnHover:
      dismissConfig?.pauseOnHover !== undefined
        ? dismissConfig.pauseOnHover
        : defaultDismiss.pauseOnHover,
  };

  // Build the notification object - ensure dismiss is set as a new object
  const notificationConfig = {
    ...defaultNotification,
    ...restProps,
    dismiss: { ...finalDismiss }, // Create a new object to ensure it's not a reference
  };

  // Debug: log the configuration to verify it's correct
  if (process.env.NODE_ENV === 'development') {
    console.log('Notification config:', {
      duration: notificationConfig.dismiss.duration,
      dismiss: notificationConfig.dismiss,
    });
  }

  try {
    Store.addNotification(notificationConfig);
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.log('addNotification error', e);
    }
  }
};

// Clear all active notifications
export const clearNotifications = () => {
  try {
    if (!Store || typeof Store.removeAllNotifications !== 'function') {
      return;
    }
    Store.removeAllNotifications();
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.log('clearNotifications error', e);
    }
  }
};
